import { Formik, getIn } from "formik";
import { Button, Card, Col, Row, Spinner } from "reactstrap";
import { validationSchema } from "../../../validations/pickList.validation";
import FormInput from "../../../components/FormInput";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QueryString from "qs";
import { toast } from "react-hot-toast";
import FormSelect from "../../../components/FormSelect";
import { getParties } from "../../../services/parties.service";
import { useEffect, useState } from "react";
import moment from "moment";
import { getPendingInvoice, updatePendingInvoice } from "../../../services/pendingInvoice.service";

interface Party {
  id: string;
}

interface InitialValues {
  party: Party | undefined;
  orderNumber: number | undefined;
  date: any;
  orderAmount: number | undefined;
  lineItem: number | undefined;
  VOR: false;
  numberOfBox: number | undefined;
}

const initialValues: InitialValues = {
  party: undefined,
  orderNumber: undefined,
  date: moment().format("YYYY-MM-DD"),
  orderAmount: undefined,
  lineItem: undefined,
  VOR: false,
  numberOfBox: undefined,
};

export default function Form() {
  const location = useLocation();
  const navigate = useNavigate();
  const params: any = useParams();
  const [initialValuesData, setInitialValuesData] = useState(initialValues);
  const [loader, setLoader] = useState(true);
  const query: any = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  useEffect(() => {
    if (params.id) {
      getPendingInvoice(params.id)
        .then((response) => {
          const picklistData = response.data;

          // Update the initialValues object with the received data
          const updatedInitialValues = {
            ...initialValues,
            party: picklistData.party || undefined,
            orderNumber: picklistData.orderNumber || null,
            date: picklistData.date
              ? moment(picklistData.date).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD"),
            orderAmount: picklistData.orderAmount || null,
            lineItem: picklistData.lineItem || null,

            VOR: picklistData.VOR || false,
            numberOfBox: picklistData.numberOfBox || null,
          };

          setInitialValuesData(updatedInitialValues);
          setLoader(false);
        })
        .catch((error) => {
          // Handle error if API call fails

          toast.error(`Error fetching picklist data: ${JSON.stringify(error)}`);
        });
    } else {
      setLoader(false);
    }
  }, [params.id]);

  function handleSubmit(values: any, actions: any) {
    const updatedData: any = {
      ...values,
      party: values.party.id,
    };
    if (params.id) {
      updatePendingInvoice(params.id, updatedData)
        .then((response: any) => {
          toast.success(response.data.message);
          navigate(query.redirect);
        })
        .catch(() => {
          actions.setSubmitting(false);
        });
    }
  }

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      {loader ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={initialValuesData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleBlur, handleChange, setFieldValue, handleSubmit }) => (
            <>
              <Row className='align-items-center mb-2'>
                <Col sm>
                  <h1 className='my-1 fw-bold h4'>Pick List</h1>
                </Col>
                <Col sm='auto'>
                  <div className='justify-content-between'>
                    <Button
                      size='sm'
                      color='secondary'
                      className='my-1 text-white me-2'
                      onClick={() => {
                        navigate(query.redirect);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size='sm'
                      color='primary'
                      className='my-1 text-white'
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </Button>
                  </div>
                </Col>
              </Row>
              <div className='flex-grow-1 overflow-auto'>
                <Card className='h-100 p-3 w-100 '>
                  <Col md={8}>
                    <div className='d-flex flex-column h-100 rounded-0'>
                      <form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormSelect
                              id='party'
                              name='party'
                              label='Party'
                              isAsync
                              getOptions={getParties}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              onBlur={handleBlur}
                              value={getIn(values, "party")}
                              error={getIn(errors, "party")}
                              touched={getIn(touched, "party")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='orderNumber'
                              label='Order Number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.orderNumber}
                              error={errors.orderNumber}
                              touched={touched.orderNumber}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormInput
                              name='date'
                              label='Date'
                              type='date'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.date}
                              error={errors.date}
                              touched={touched.date}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='orderAmount'
                              label='Order Amount'
                              type='number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.orderAmount}
                              error={errors.orderAmount}
                              touched={touched.orderAmount}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormInput
                              name='lineItem'
                              label='Line Item'
                              type='number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.lineItem}
                              error={errors.lineItem}
                              touched={touched.lineItem}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='numberOfBox'
                              label='Number of Box'
                              type='number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.numberOfBox}
                              error={errors.numberOfBox}
                              touched={touched.numberOfBox}
                            />
                          </Col>
                          <Col md={6}>
                            <div className='form-check'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                id='VOR'
                                name='VOR'
                                onChange={handleChange}
                                onBlur={handleBlur}
                                checked={values.VOR}
                              />
                              <label className='form-check-label' htmlFor='VOR'>
                                VOR
                              </label>
                            </div>
                          </Col>
                        </Row>
                      </form>
                    </div>
                  </Col>
                </Card>
              </div>
            </>
          )}
        </Formik>
      )}
    </div>
  );
}
